import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { DrillValue } from '../../components/Drill'
import { Link } from 'react-router-dom'
import {
  checkAdhocAudience,
  getEmailAudience,
  getEmailCampaign,
  listEmailCampaigns,
  previewAdhocEmail,
  resumeEmailCampaign,
  sendAdhocEmail,
  stopEmailCampaign,
  testAdhocEmail,
  type AudienceFacts,
  type CampaignContent,
  type CampaignDetail,
  type CampaignPurpose,
  type CampaignSummary,
  type LeftOut,
  type RecipientStatus
} from '../../lib/adminClient'
import type { AdminUser } from '../../lib/domain'
import { useRemoteUsers } from '../../lib/useRemoteData'
import { useTableControls, type Column } from '../../lib/useTableControls'
import { NoMatches, TableHead, TableToolbar } from '../../components/TableControls'
import { LoadError } from '../../components/LoadError'
import { agoBucket, billingLabel, trialBadge, verifiedLabel } from '../../lib/userLabels'
import { dateTime, day, num } from '../../lib/format'

// Write an email and send it to the accounts you choose (server:
// emailCampaigns.ts). Owner, 2026-10-30: "allow sending ad hoc emails to either
// single users, or groups selected with bulk select options and filters".
//
// Choosing works like every table in the console: filter by any column, then
// choose everyone shown, or tick people one by one. The Users page's Email
// button lands here with that one person chosen. Nothing is sent until the
// confirm step, which shows exactly how many will get it and who is left out
// and why; the server refuses the send if that number has changed since.

const inputClass =
  'w-full rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-accent/50 disabled:opacity-60'

const DRAFT_KEY = 'plexii-admin.adhoc-draft.v1'
const PAGE = 100

interface Draft {
  purpose: CampaignPurpose
  subject: string
  body: string
  buttonLabel: string
  buttonUrl: string
}
const EMPTY_DRAFT: Draft = { purpose: 'announcement', subject: '', body: '', buttonLabel: '', buttonUrl: '' }

function readDraft(): Draft {
  try {
    const raw = localStorage.getItem(DRAFT_KEY)
    if (raw) return { ...EMPTY_DRAFT, ...(JSON.parse(raw) as Partial<Draft>) }
  } catch {
    // storage unavailable: start empty
  }
  return EMPTY_DRAFT
}

const toContent = (d: Draft): CampaignContent => ({
  subject: d.subject,
  paragraphs: d.body.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean),
  button: d.buttonLabel.trim() || d.buttonUrl.trim() ? { label: d.buttonLabel, url: d.buttonUrl } : null
})

// ── The recipient picker's rows ─────────────────────────────────────────────

interface Pick {
  u: AdminUser
  chosen: boolean
  marketing: string
  lastEmailed: string
  sent30: number
}

function trialState(u: AdminUser): string {
  const l = trialBadge(u).label
  if (l.endsWith('d left')) return 'In trial'
  if (l === 'Starts on verify') return 'Starts on verify'
  if (l === 'Lapsed') return 'Trial ended'
  return 'No trial'
}

const PICK_COLUMNS: Column<Pick>[] = [
  { key: 'chosen', label: 'Chosen', value: (p) => (p.chosen ? 'Chosen' : 'Not chosen') },
  { key: 'user', label: 'User', value: (p) => `${p.u.handle ?? ''} ${p.u.email}`.trim(), filter: 'text' },
  { key: 'tier', label: 'Tier', value: (p) => p.u.tier },
  { key: 'billing', label: 'Billing', value: (p) => billingLabel(p.u) },
  { key: 'verified', label: 'Email', value: (p) => verifiedLabel(p.u) },
  { key: 'trial', label: 'Trial', value: (p) => trialState(p.u) },
  { key: 'marketing', label: 'Marketing', value: (p) => p.marketing },
  { key: 'lastEmailed', label: 'Last emailed', value: (p) => p.lastEmailed },
  { key: 'joined', label: 'Joined', value: (p) => day(p.u.createdAt).slice(0, 7) },
  { key: 'lastLogin', label: 'Last login', value: (p) => agoBucket(p.u.lastLoginAt) },
  { key: 'status', label: 'Status', value: (p) => p.u.status },
  { key: 'client', label: 'App version', value: (p) => p.u.telemetryVersion ?? 'Not reported' },
  { key: 'onboarded', label: 'Onboarded', value: (p) => (p.u.onboardingCompleted === true ? 'Yes' : p.u.onboardingCompleted === false ? 'No' : 'Not reported') }
]

const RECIPIENT_WORDS: Record<RecipientStatus, string> = {
  pending: 'Waiting',
  sending: 'Sending',
  sent: 'Sent',
  failed: 'Failed',
  not_sent: 'Not sent',
  skipped: 'Left out',
  cancelled: 'Stopped',
  unknown: 'Unknown'
}

const CAMPAIGN_WORDS: Record<CampaignSummary['status'], string> = {
  sending: 'Sending',
  paused: 'Paused',
  cancelled: 'Stopped',
  done: 'Finished'
}

export default function SendEmail({
  canWrite,
  preselect,
  openCampaignId,
  onOpenCampaign
}: {
  canWrite: boolean
  preselect: string | null
  openCampaignId: string | null
  onOpenCampaign: (id: string | null) => void
}): JSX.Element {
  const users = useRemoteUsers()
  const [audience, setAudience] = useState<AudienceFacts | null>(null)
  const [audienceError, setAudienceError] = useState<string | null>(null)
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(preselect ? [preselect] : []))
  const [shown, setShown] = useState(PAGE)
  const [openPick, setOpenPick] = useState<string | null>(null)
  const [draft, setDraftState] = useState<Draft>(readDraft)
  const [history, setHistory] = useState<CampaignSummary[] | null>(null)
  const [historyError, setHistoryError] = useState<string | null>(null)

  useEffect(() => {
    void getEmailAudience().then((r) => (r.ok ? setAudience(r.data) : setAudienceError(r.error)))
  }, [])

  const loadHistory = useCallback(async () => {
    const r = await listEmailCampaigns()
    if (r.ok) {
      setHistory(r.data.campaigns)
      setHistoryError(null)
    } else setHistoryError(r.error)
  }, [])
  useEffect(() => { void loadHistory() }, [loadHistory])

  const setDraft = (patch: Partial<Draft>): void =>
    setDraftState((d) => {
      const next = { ...d, ...patch }
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify(next))
      } catch {
        // storage unavailable: the draft just isn't kept
      }
      return next
    })

  const picks = useMemo<Pick[]>(
    () =>
      users.data.map((u) => {
        const f = audience?.facts[u.id]
        return {
          u,
          chosen: chosen.has(u.id),
          marketing: f ? (f.unsubscribed ? 'Unsubscribed' : 'Subscribed') : 'Unknown',
          lastEmailed: f ? agoBucket(f.lastEmailedAt) : 'Unknown',
          sent30: f?.sent30 ?? 0
        }
      }),
    [users.data, audience, chosen]
  )
  const t = useTableControls(picks, PICK_COLUMNS, 'email-recipients')
  useEffect(() => setShown(PAGE), [t.search, t.filters, t.textFilters])

  // Arriving from the Users page with one person: show just them.
  const preselectApplied = useRef(false)
  useEffect(() => {
    if (!preselect || preselectApplied.current || users.loading) return
    preselectApplied.current = true
    if (users.data.some((u) => u.id === preselect)) t.setFilter('chosen', ['Chosen'])
  }, [preselect, users.loading, users.data, t])

  const chosenUsers = useMemo(() => users.data.filter((u) => chosen.has(u.id)), [users.data, chosen])
  const visible = t.rows.slice(0, shown)

  const change = (ids: string[], on: boolean): void =>
    setChosen((prev) => {
      const next = new Set(prev)
      for (const id of ids) {
        if (on) next.add(id)
        else next.delete(id)
      }
      return next
    })

  const max = audience?.maxRecipients ?? 10_000
  const content = toContent(draft)

  return (
    // minmax(0, 1fr): a wide table scrolls inside its own box instead of
    // stretching the page sideways.
    <div className="grid grid-cols-[minmax(0,1fr)] gap-6">
      {/* ── 1. Who ─────────────────────────────────────────────────────── */}
      <section data-testid="send-who">
        <div className="mb-2">
          <h2 className="text-base font-semibold text-slate-100">1. Who gets it</h2>
          <div className="text-xs text-slate-400">
            Filter by any column, then choose everyone shown, or tick people one at a time. For one person, use their Email button on
            the Users page.
          </div>
        </div>
        {users.error && <LoadError what="users" endpoint="GET /admin/users" error={users.error} testId="send-users-error" />}
        {audienceError && (
          <div className="text-xs text-amber-200 mb-2" data-testid="send-audience-error">
            Could not load who unsubscribed and when we last wrote ({audienceError}). Those columns show Unknown; the send still leaves out
            anyone who unsubscribed.
          </div>
        )}
        <TableToolbar t={t} placeholder="Search name or email…" noun="accounts">
          <span className="text-xs px-2 py-1 rounded-md bg-accent/15 text-violet-100 tabular-nums" data-testid="send-chosen-count">
            {num(chosen.size)} chosen
          </span>
          <button className="btn-ghost text-xs" onClick={() => change(t.rows.map((p) => p.u.id), true)} disabled={t.rows.length === 0} data-testid="send-choose-shown">
            Choose all {num(t.rows.length)} shown
          </button>
          <button className="btn-ghost text-xs" onClick={() => change(t.rows.map((p) => p.u.id), false)} disabled={!t.rows.some((p) => p.chosen)} data-testid="send-unchoose-shown">
            Unchoose shown
          </button>
          <button className="btn-ghost text-xs" onClick={() => setChosen(new Set())} disabled={chosen.size === 0} data-testid="send-clear-chosen">
            Clear choice
          </button>
          <button
            className="btn-ghost text-xs"
            onClick={() => t.setFilter('chosen', t.filters.chosen?.[0] === 'Chosen' ? [] : ['Chosen'])}
            data-testid="send-show-chosen"
          >
            {t.filters.chosen?.[0] === 'Chosen' ? 'Show everyone' : 'Show chosen only'}
          </button>
        </TableToolbar>
        {users.loading ? (
          <div className="glass-card rounded-xl p-6 text-sm text-slate-400">Loading accounts…</div>
        ) : t.rows.length === 0 ? (
          users.data.length === 0 ? <div className="glass-card rounded-xl p-6 text-sm text-slate-400">No accounts yet.</div> : <NoMatches t={t} noun="accounts" />
        ) : (
          <div className="glass-card rounded-xl overflow-x-auto max-h-[460px] overflow-y-auto">
            <table className="admin-table" data-testid="send-picker">
              <TableHead t={t} />
              <tbody>
                {visible.map((p) => (
                  <PickRow key={p.u.id} p={p} open={openPick === p.u.id} onToggle={() => setOpenPick(openPick === p.u.id ? null : p.u.id)} onChoose={() => change([p.u.id], !p.chosen)} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {t.rows.length > shown && (
          <div className="mt-2 flex items-center gap-3 text-xs text-slate-400">
            <span>Showing {num(shown)} of {num(t.rows.length)}. “Choose all shown” takes all {num(t.rows.length)}.</span>
            <button className="btn-ghost text-xs" onClick={() => setShown(shown + PAGE)} data-testid="send-more">Show {PAGE} more</button>
          </div>
        )}
        {chosen.size > max && (
          <div className="text-xs text-red-300 mt-2" data-testid="send-too-many">One send can go to at most {num(max)} people. Narrow the choice.</div>
        )}
      </section>

      {/* ── 2. What ────────────────────────────────────────────────────── */}
      <Composer
        draft={draft}
        setDraft={setDraft}
        canWrite={canWrite}
        // Preview as the first chosen person who will actually get it.
        as={chosenUsers.find((u) => !(draft.purpose === 'announcement' && audience?.facts[u.id]?.unsubscribed)) ?? null}
        canAnnounce={audience?.canSendAnnouncements ?? true}
      />

      {/* ── 3. Send ────────────────────────────────────────────────────── */}
      <SendStep
        canWrite={canWrite}
        purpose={draft.purpose}
        content={content}
        chosenIds={[...chosen]}
        tooMany={chosen.size > max}
        onSent={(c) => {
          onOpenCampaign(c.id)
          void loadHistory()
        }}
      />

      {/* ── Earlier sends ──────────────────────────────────────────────── */}
      <section data-testid="send-history">
        <div className="mb-2 flex items-center gap-3">
          <h2 className="text-base font-semibold text-slate-100">Sent from here</h2>
          <button className="btn-ghost text-xs" onClick={() => void loadHistory()}>Refresh</button>
        </div>
        {historyError && <LoadError what="earlier sends" endpoint="GET /admin/emails/campaigns" error={historyError} testId="send-history-error" />}
        {/* Opened from the log or just sent, and not in the list yet: shown on top. */}
        {openCampaignId && !history?.some((c) => c.id === openCampaignId) && (
          <CampaignPanel id={openCampaignId} canWrite={canWrite} onClose={() => onOpenCampaign(null)} onChanged={() => void loadHistory()} />
        )}
        {history && history.length === 0 && <div className="glass-card rounded-xl p-4 text-sm text-slate-400">Nothing has been sent from here yet.</div>}
        {history && history.length > 0 && (
          <div className="glass-card rounded-xl divide-y divide-white/5">
            {history.map((c) => (
              <div key={c.id}>
              <button
                className={`w-full text-left p-3 flex flex-wrap items-center gap-3 hover:bg-white/[0.02] ${openCampaignId === c.id ? 'bg-white/[0.03]' : ''}`}
                onClick={() => onOpenCampaign(openCampaignId === c.id ? null : c.id)}
                aria-expanded={openCampaignId === c.id}
                data-testid={`send-history-${c.id}`}
              >
                <span className={`text-[11px] px-1.5 py-0.5 rounded ${c.status === 'done' ? 'bg-emerald-500/15 text-emerald-200' : c.status === 'paused' ? 'bg-amber-500/15 text-amber-200' : c.status === 'cancelled' ? 'bg-white/5 text-slate-300' : 'bg-sky-500/15 text-sky-200'}`}>
                  {CAMPAIGN_WORDS[c.status]}
                </span>
                <span className="text-sm text-slate-100 min-w-0 flex-1 truncate">{c.subject}</span>
                <span className="text-xs text-slate-400 tabular-nums">
                  {num(c.counts.sent)} sent{c.counts.failed + c.counts.not_sent ? ` · ${num(c.counts.failed + c.counts.not_sent)} failed` : ''}
                  {c.counts.skipped ? ` · ${num(c.counts.skipped)} left out` : ''}
                </span>
                <span className="text-xs text-slate-500">{c.purpose === 'announcement' ? 'Announcement' : 'Account notice'} · {c.createdBy} · {dateTime(c.createdAt)}</span>
              </button>
              {openCampaignId === c.id && (
                <div className="px-3 pb-3">
                  <CampaignPanel id={c.id} canWrite={canWrite} onClose={() => onOpenCampaign(null)} onChanged={() => void loadHistory()} />
                </div>
              )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

// ── One account in the picker, and what it opens to ─────────────────────────

const logLink = (email: string): string => `/emails?tab=log&q=${encodeURIComponent(email)}`

function Fact({ k, v }: { k: string; v: React.ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <span className="text-slate-500">{k}: </span>
      <span className="text-slate-200 break-words">{v}</span>
    </div>
  )
}

function PickRow({ p, open, onToggle, onChoose }: { p: Pick; open: boolean; onToggle: () => void; onChoose: () => void }): JSX.Element {
  const u = p.u
  return (
    <>
      <tr className="cursor-pointer" onClick={onToggle} aria-expanded={open} data-testid={`send-pick-${u.id}`}>
        <td onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" aria-label={`Choose ${u.email}`} checked={p.chosen} onChange={onChoose} data-testid={`send-check-${u.id}`} />
        </td>
        <td>
          <div className="text-slate-100 text-sm">{u.handle ?? u.email.split('@')[0]}</div>
          <div className="text-xs text-slate-400 whitespace-nowrap">{u.email}</div>
        </td>
        <td className="text-xs">{u.tier}</td>
        <td className="text-xs">{billingLabel(u)}</td>
        <td className="text-xs">{verifiedLabel(u)}</td>
        <td className="text-xs">{trialState(u)}</td>
        <td className="text-xs">
          <span className={p.marketing === 'Unsubscribed' ? 'text-amber-200' : 'text-slate-300'}>{p.marketing}</span>
        </td>
        <td className="text-xs text-slate-400">{p.lastEmailed}</td>
        <td className="text-xs text-slate-400">{day(u.createdAt)}</td>
        <td className="text-xs text-slate-400">{agoBucket(u.lastLoginAt)}</td>
        <td className="text-xs">{u.status}</td>
        <td className="text-xs text-slate-400">{u.telemetryVersion ?? '—'}</td>
        <td className="text-xs text-slate-400">{u.onboardingCompleted === true ? 'Yes' : u.onboardingCompleted === false ? 'No' : '—'}</td>
      </tr>
      {open && (
        <tr data-testid={`send-pick-detail-${u.id}`}>
          <td colSpan={PICK_COLUMNS.length} className="bg-white/[0.02]">
            <div className="p-3 grid gap-2 text-xs sm:grid-cols-2 lg:grid-cols-3">
              <Fact k="Email" v={u.email} />
              <Fact k="Handle" v={u.handle ?? '—'} />
              <Fact k="Account id" v={u.id} />
              <Fact k="Tier" v={u.tier} />
              <Fact k="Billing" v={u.billing?.paying === true ? `Paying${u.billing.subscriptionStatus ? ` (${u.billing.subscriptionStatus})` : ''}` : billingLabel(u)} />
              <Fact k="Email confirmed" v={u.emailVerified === true ? `Yes${u.emailVerifiedAt ? `, ${day(u.emailVerifiedAt)}` : ''}` : verifiedLabel(u)} />
              <Fact k="Trial" v={`${trialState(u)}${u.trialExpiresAt ? `, ends ${day(u.trialExpiresAt)}` : ''}`} />
              <Fact k="Marketing emails" v={p.marketing === 'Unsubscribed' ? 'Unsubscribed: announcements skip them' : p.marketing} />
              <Fact
                k="Emails in the last 30 days"
                v={
                  <DrillValue to={{ set: 'emails', filters: { toEmail: u.email, fromMs: String(Date.now() - 30 * 86_400_000), notKind: 'admin_test' } }} label={`Emails to ${u.email} in the last 30 days`} expect={{ key: 'count', value: p.sent30 }}>
                    {num(p.sent30)}
                  </DrillValue>
                }
              />
              <Fact k="Joined" v={day(u.createdAt)} />
              <Fact k="Last login" v={u.lastLoginAtMs ? dateTime(u.lastLoginAtMs) : u.lastLoginAt ?? 'Never'} />
              <Fact k="Status" v={u.status} />
              <Fact k="Sign-ins" v={num(u.deviceCount)} />
              <Fact k="App" v={u.telemetryVersion ? `${u.telemetryVersion}${u.telemetryPlatform ? ` on ${u.telemetryPlatform}` : ''}` : 'Not reported'} />
              <Fact k="Onboarded" v={u.onboardingCompleted === true ? 'Yes' : u.onboardingCompleted === false ? 'No' : 'Not reported'} />
              <div className="sm:col-span-2 lg:col-span-3 flex flex-wrap gap-2 pt-1">
                <button className="btn-ghost text-xs" onClick={onChoose} data-testid={`send-pick-choose-${u.id}`}>{p.chosen ? 'Unchoose' : 'Choose'}</button>
                <Link className="btn-ghost text-xs" to={logLink(u.email)} data-testid={`send-pick-log-${u.id}`}>Emails sent to them</Link>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

// ── The composer and its preview ────────────────────────────────────────────

function Composer({
  draft,
  setDraft,
  canWrite,
  as,
  canAnnounce
}: {
  draft: Draft
  setDraft: (p: Partial<Draft>) => void
  canWrite: boolean
  as: AdminUser | null
  canAnnounce: boolean
}): JSX.Element {
  const [preview, setPreview] = useState<{ subject: string; html: string } | { error: string } | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!draft.subject.trim() && !draft.body.trim()) return setPreview(null)
      void previewAdhocEmail({ purpose: draft.purpose, content: toContent(draft), ...(as ? { accountId: as.id } : {}) }).then((r) =>
        setPreview(r.ok ? { subject: r.data.subject, html: r.data.html } : { error: r.error })
      )
    }, 400)
    return () => clearTimeout(timer)
  }, [draft, as])

  const purposeOption = (value: CampaignPurpose, title: string, note: string): JSX.Element => (
    <label className={`flex-1 min-w-[15rem] rounded-lg border p-3 cursor-pointer ${draft.purpose === value ? 'border-accent/50 bg-accent/10' : 'border-white/10'}`}>
      <div className="flex items-center gap-2">
        <input type="radio" name="purpose" checked={draft.purpose === value} onChange={() => setDraft({ purpose: value })} data-testid={`send-purpose-${value}`} />
        <span className="text-sm text-slate-100">{title}</span>
      </div>
      <div className="text-xs text-slate-400 mt-1">{note}</div>
    </label>
  )

  return (
    <section data-testid="send-what">
      <div className="mb-2">
        <h2 className="text-base font-semibold text-slate-100">2. What it says</h2>
        <div className="text-xs text-slate-400">Your draft is kept in this browser until you send it.</div>
      </div>
      <div className="grid gap-4 grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="glass-card rounded-xl p-4 grid gap-3 content-start">
          <div className="flex flex-wrap gap-2">
            {purposeOption('announcement', 'Announcement', 'News, offers, product updates. Has an unsubscribe link and skips anyone who unsubscribed.')}
            {purposeOption('notice', 'Account notice', 'Only about their account or the service, like downtime or a policy change. Goes to everyone chosen.')}
          </div>
          {draft.purpose === 'announcement' && !canAnnounce && (
            <div className="text-xs text-red-300">The server has no unsubscribe secret, so announcements cannot be sent. Account notices still can.</div>
          )}
          <label className="grid gap-1">
            <span className="text-xs text-slate-400">Subject</span>
            <input className={inputClass} value={draft.subject} onChange={(e) => setDraft({ subject: e.target.value })} maxLength={150} data-testid="send-subject" />
          </label>
          <label className="grid gap-1">
            <span className="text-xs text-slate-400">Message: leave a blank line between paragraphs</span>
            <textarea className={`${inputClass} min-h-[180px]`} value={draft.body} onChange={(e) => setDraft({ body: e.target.value })} data-testid="send-body" />
          </label>
          <div className="grid gap-2 sm:grid-cols-[1fr_2fr]">
            <label className="grid gap-1">
              <span className="text-xs text-slate-400">Button label (optional)</span>
              <input className={inputClass} value={draft.buttonLabel} onChange={(e) => setDraft({ buttonLabel: e.target.value })} maxLength={40} data-testid="send-button-label" />
            </label>
            <label className="grid gap-1">
              <span className="text-xs text-slate-400">Button link</span>
              <input className={inputClass} value={draft.buttonUrl} onChange={(e) => setDraft({ buttonUrl: e.target.value })} placeholder="https://plexiidesk.com/…" data-testid="send-button-url" />
            </label>
          </div>
          <div className="text-xs text-slate-500">
            Type <code className="text-slate-300">{'{first_name}'}</code> for their first name (“there” when we don’t have one). The footer with the
            company details{draft.purpose === 'announcement' ? ' and the unsubscribe link' : ''} is always added.
          </div>
          {!canWrite && <div className="text-xs text-amber-200">You can write and preview, but sending needs an admin account.</div>}
        </div>
        <div className="glass-card rounded-xl p-3 min-w-0">
          <div className="text-xs text-slate-400 mb-2" data-testid="send-preview-as">
            {as ? `Preview as ${as.handle ?? as.email} (${as.email})` : 'Preview as you. Choose people to see it as the first of them.'}
          </div>
          {preview === null && <div className="text-sm text-slate-500 p-6">Write a subject and a message to see it here.</div>}
          {preview && 'error' in preview && <div className="text-sm text-amber-200 p-3" data-testid="send-preview-error">{preview.error}</div>}
          {preview && 'html' in preview && (
            <>
              <div className="text-sm mb-2"><span className="text-slate-500">Subject: </span><span className="text-slate-100" data-testid="send-preview-subject">{preview.subject}</span></div>
              <iframe title="Email preview" sandbox="" srcDoc={preview.html} className="w-full h-[520px] bg-white rounded" data-testid="send-preview-frame" />
            </>
          )}
        </div>
      </div>
    </section>
  )
}

// ── Test, review, confirm ───────────────────────────────────────────────────

function SendStep({
  canWrite,
  purpose,
  content,
  chosenIds,
  tooMany,
  onSent
}: {
  canWrite: boolean
  purpose: CampaignPurpose
  content: CampaignContent
  chosenIds: string[]
  tooMany: boolean
  onSent: (c: CampaignDetail) => void
}): JSX.Element {
  const [busy, setBusy] = useState<'test' | 'check' | 'send' | null>(null)
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)
  const [review, setReview] = useState<{ willSend: number; skipped: LeftOut[]; key: string } | null>(null)
  const key = JSON.stringify([purpose, content, [...chosenIds].sort()])
  // Any change after reviewing means reviewing again.
  const current = review && review.key === key ? review : null

  async function test(): Promise<void> {
    setBusy('test')
    setMessage(null)
    const r = await testAdhocEmail({ purpose, content })
    setBusy(null)
    setMessage(r.ok ? { tone: 'ok', text: r.data.delivered ? `Test sent to ${r.data.to}.` : `The server has no email provider, so the test to ${r.data.to} was only logged.` } : { tone: 'error', text: r.error })
  }

  async function check(): Promise<void> {
    setBusy('check')
    setMessage(null)
    const r = await checkAdhocAudience(purpose, chosenIds)
    setBusy(null)
    if (r.ok) setReview({ willSend: r.data.willSend, skipped: r.data.skipped, key })
    else setMessage({ tone: 'error', text: r.error })
  }

  async function send(): Promise<void> {
    if (!current) return
    setBusy('send')
    setMessage(null)
    const r = await sendAdhocEmail({ purpose, content, accountIds: chosenIds, confirmCount: current.willSend })
    setBusy(null)
    if (r.ok) {
      setReview(null)
      setMessage({ tone: 'ok', text: `Sending to ${num(r.data.campaign.counts.pending)} ${r.data.campaign.counts.pending === 1 ? 'person' : 'people'}. Progress is below.` })
      try {
        localStorage.removeItem(DRAFT_KEY)
      } catch {
        // ignore
      }
      onSent(r.data.campaign)
      return
    }
    if (r.check) setReview({ willSend: r.check.willSend, skipped: r.check.skipped, key })
    setMessage({ tone: 'error', text: r.error })
  }

  const ready = content.subject.trim() !== '' && content.paragraphs.length > 0
  return (
    <section data-testid="send-step">
      <div className="mb-2">
        <h2 className="text-base font-semibold text-slate-100">3. Send</h2>
        <div className="text-xs text-slate-400">Send yourself a test first. Then review who gets it, and confirm.</div>
      </div>
      <div className="glass-card rounded-xl p-4 grid gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button className="btn-ghost" onClick={() => void test()} disabled={!canWrite || !ready || busy !== null} data-testid="send-test">
            {busy === 'test' ? 'Sending…' : 'Send me a test'}
          </button>
          <button className="btn-primary" onClick={() => void check()} disabled={!canWrite || !ready || chosenIds.length === 0 || tooMany || busy !== null} data-testid="send-review">
            {busy === 'check' ? 'Checking…' : `Review: ${num(chosenIds.length)} chosen`}
          </button>
          {!ready && <span className="text-xs text-slate-500">Write a subject and a message first.</span>}
          {ready && chosenIds.length === 0 && <span className="text-xs text-slate-500">Choose who gets it above.</span>}
        </div>
        {message && (
          <div className={`text-sm ${message.tone === 'ok' ? 'text-emerald-200' : 'text-red-300'}`} role="status" data-testid="send-message">
            {message.text}
          </div>
        )}
        {current && (
          <div className="rounded-lg border border-accent/30 bg-accent/5 p-3 grid gap-2" data-testid="send-confirm">
            <div className="text-sm text-slate-100">
              <strong data-testid="send-confirm-count">{num(current.willSend)}</strong> {current.willSend === 1 ? 'person gets' : 'people get'} “{content.subject}”
              {purpose === 'announcement' ? ' as an announcement.' : ' as an account notice.'}
            </div>
            {current.skipped.length > 0 && (
              <details data-testid="send-confirm-skipped">
                <summary className="text-xs text-amber-200 cursor-pointer">
                  {num(current.skipped.length)} left out ({summarise(current.skipped)})
                </summary>
                <ul className="mt-1 text-xs text-slate-300 max-h-40 overflow-y-auto">
                  {current.skipped.map((s) => (
                    <li key={s.accountId}>{s.email ?? s.accountId}: {s.reason}</li>
                  ))}
                </ul>
              </details>
            )}
            <div className="flex flex-wrap gap-2">
              <button className="btn-primary" onClick={() => void send()} disabled={busy !== null || current.willSend === 0} data-testid="send-confirm-go">
                {busy === 'send' ? 'Starting…' : `Send to ${num(current.willSend)} ${current.willSend === 1 ? 'person' : 'people'}`}
              </button>
              <button className="btn-ghost" onClick={() => setReview(null)} disabled={busy !== null}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

function summarise(list: LeftOut[]): string {
  const by = new Map<string, number>()
  for (const s of list) by.set(s.reason, (by.get(s.reason) ?? 0) + 1)
  return [...by].map(([r, n]) => `${num(n)} ${r.toLowerCase()}`).join('; ')
}

// ── One send, with its people ───────────────────────────────────────────────

const RECIPIENT_COLUMNS: Column<CampaignDetail['recipients'][number]>[] = [
  { key: 'who', label: 'Who', value: (r) => `${r.name ?? ''} ${r.email}`.trim(), filter: 'text' },
  { key: 'status', label: 'Status', value: (r) => RECIPIENT_WORDS[r.status] },
  { key: 'reason', label: 'Reason', value: (r) => r.reason ?? '', filter: 'text' },
  { key: 'updated', label: 'When', value: (r) => dateTime(r.updatedAt), filter: 'text' }
]

function CampaignPanel({ id, canWrite, onClose, onChanged }: { id: string; canWrite: boolean; onClose: () => void; onChanged: () => void }): JSX.Element {
  const [c, setC] = useState<CampaignDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirmStop, setConfirmStop] = useState(false)
  const [openPerson, setOpenPerson] = useState<string | null>(null)

  const load = useCallback(async () => {
    const r = await getEmailCampaign(id)
    if (r.ok) {
      setC(r.data.campaign)
      setError(null)
    } else setError(r.error)
  }, [id])
  useEffect(() => {
    setC(null)
    void load()
  }, [load])
  // Follow a send while it goes out.
  useEffect(() => {
    if (c?.status !== 'sending') return
    const timer = setInterval(() => void load(), 2000)
    return () => clearInterval(timer)
  }, [c?.status, load])
  const wasSending = useRef(false)
  useEffect(() => {
    if (wasSending.current && c && c.status !== 'sending') onChanged()
    wasSending.current = c?.status === 'sending'
  }, [c, onChanged])

  const t = useTableControls(c?.recipients ?? null, RECIPIENT_COLUMNS, 'email-campaign-recipients')

  async function act(kind: 'stop' | 'resume'): Promise<void> {
    setBusy(true)
    const r = kind === 'stop' ? await stopEmailCampaign(id) : await resumeEmailCampaign(id)
    setBusy(false)
    setConfirmStop(false)
    if (r.ok) {
      setC(r.data.campaign)
      onChanged()
    } else setError(r.error)
  }

  const done = c ? c.counts.sent + c.counts.failed + c.counts.not_sent + c.counts.unknown : 0
  const toGo = c ? c.counts.pending + c.counts.sending : 0
  return (
    <div className="glass-card rounded-xl p-4 mb-3 border border-accent/20" data-testid="send-campaign">
      <div className="flex flex-wrap items-start gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-slate-100 font-medium">{c?.subject ?? 'Loading…'}</div>
          {c && (
            <div className="text-xs text-slate-400 mt-0.5">
              {c.purpose === 'announcement' ? 'Announcement' : 'Account notice'} · sent by {c.createdBy} · {dateTime(c.createdAt)}
              {c.finishedAt ? ` · finished ${dateTime(c.finishedAt)}` : ''}
            </div>
          )}
        </div>
        <button className="btn-ghost text-xs" onClick={onClose}>Close</button>
      </div>
      {error && <div className="text-sm text-red-300 mb-2">{error}</div>}
      {c && (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-2 text-sm" data-testid="send-campaign-status">
            <span className="text-slate-100">{CAMPAIGN_WORDS[c.status]}</span>
            <span className="text-slate-400 tabular-nums">
              {/* Each count opens the people it counts, with what happened to each copy. */}
              <RecipientsValue c={c} status="sent" n={c.counts.sent} words="sent" /> ·{' '}
              <RecipientsValue c={c} status="failed,not_sent" n={c.counts.failed + c.counts.not_sent} words="failed" /> ·{' '}
              <RecipientsValue c={c} status="pending,sending" n={toGo} words="to go" /> ·{' '}
              <RecipientsValue c={c} status="skipped" n={c.counts.skipped} words="left out" />
              {c.counts.cancelled ? <> · <RecipientsValue c={c} status="cancelled" n={c.counts.cancelled} words="stopped" /></> : null}
              {c.counts.unknown ? <> · <RecipientsValue c={c} status="unknown" n={c.counts.unknown} words="unknown" /></> : null}
            </span>
          </div>
          {c.status === 'sending' && c.counts.pending + c.counts.sending + done > 0 && (
            <div className="h-1.5 rounded bg-white/5 overflow-hidden mb-2">
              <div className="h-full bg-accent" style={{ width: `${Math.round((done / (done + toGo)) * 100)}%` }} />
            </div>
          )}
          {c.status === 'paused' && c.pausedReason && <div className="text-sm text-amber-200 mb-2" data-testid="send-campaign-paused">{c.pausedReason}</div>}
          {canWrite && (c.status === 'sending' || c.status === 'paused') && (
            <div className="flex flex-wrap gap-2 mb-3">
              {c.status === 'paused' && (
                <button className="btn-primary text-xs" onClick={() => void act('resume')} disabled={busy} data-testid="send-campaign-resume">Resume</button>
              )}
              {!confirmStop ? (
                <button className="btn-ghost text-xs" onClick={() => setConfirmStop(true)} disabled={busy} data-testid="send-campaign-stop">Stop sending</button>
              ) : (
                <>
                  <span className="text-xs text-amber-200 self-center">Stop? The {num(toGo)} not reached yet won’t get it.</span>
                  <button className="btn-ghost text-xs" onClick={() => void act('stop')} disabled={busy} data-testid="send-campaign-stop-confirm">Yes, stop</button>
                  <button className="btn-ghost text-xs" onClick={() => setConfirmStop(false)}>Keep sending</button>
                </>
              )}
            </div>
          )}
          <details className="mb-3" data-testid="send-campaign-content">
            <summary className="text-xs text-slate-300 cursor-pointer">What it said</summary>
            <div className="mt-2 rounded-lg border border-white/10 p-3 text-sm text-slate-200 grid gap-2">
              <div><span className="text-slate-500">Subject: </span>{c.content.subject}</div>
              {c.content.paragraphs.map((x, i) => <p key={i} className="text-slate-300">{x}</p>)}
              {c.content.button && <div className="text-xs"><span className="text-slate-500">Button: </span>{c.content.button.label} → <span className="break-all">{c.content.button.url}</span></div>}
            </div>
          </details>
          <TableToolbar t={t} placeholder="Search name, email, reason…" noun="people" />
          {t.rows.length === 0 ? (
            <NoMatches t={t} noun="people" />
          ) : (
            <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
              <table className="admin-table" data-testid="send-campaign-people">
                <TableHead t={t} />
                <tbody>
                  {t.rows.map((r) => (
                    <PersonRow key={r.accountId} r={r} open={openPerson === r.accountId} onToggle={() => setOpenPerson(openPerson === r.accountId ? null : r.accountId)} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function PersonRow({ r, open, onToggle }: { r: CampaignDetail['recipients'][number]; open: boolean; onToggle: () => void }): JSX.Element {
  return (
    <>
      <tr className="cursor-pointer" onClick={onToggle} aria-expanded={open} data-testid={`send-person-${r.accountId}`}>
        <td>
          <div className="text-sm text-slate-100">{r.name ?? r.email}</div>
          {r.name && <div className="text-xs text-slate-400">{r.email}</div>}
        </td>
        <td className="text-xs">{RECIPIENT_WORDS[r.status]}</td>
        <td className="text-xs text-slate-400">{r.reason ?? ''}</td>
        <td className="text-xs text-slate-500 tabular-nums">{dateTime(r.updatedAt)}</td>
      </tr>
      {open && (
        <tr data-testid={`send-person-detail-${r.accountId}`}>
          <td colSpan={RECIPIENT_COLUMNS.length} className="bg-white/[0.02]">
            <div className="p-3 grid gap-2 text-xs sm:grid-cols-2">
              <Fact k="Email" v={r.email} />
              <Fact k="Name" v={r.name ?? '—'} />
              <Fact k="Status" v={RECIPIENT_WORDS[r.status]} />
              <Fact k="Last change" v={dateTime(r.updatedAt)} />
              {r.reason && <div className="sm:col-span-2"><Fact k="Why" v={r.reason} /></div>}
              <Fact k="Account id" v={r.accountId} />
              <Fact k="Log id" v={r.sendId ?? 'Not sent, so not in the log'} />
              <div className="sm:col-span-2 pt-1">
                <Link className="btn-ghost text-xs" to={logLink(r.email)} data-testid={`send-person-log-${r.accountId}`}>Emails sent to them</Link>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

function RecipientsValue({ c, status, n, words }: { c: { id: string; subject: string }; status: string; n: number; words: string }): JSX.Element {
  return (
    <DrillValue to={{ set: 'campaignRecipients', filters: { campaign: c.id, status } }} label={`“${c.subject}”: ${words}`} expect={{ key: 'count', value: n }} testId={`campaign-${status.split(',')[0]}`}>
      {num(n)} {words}
    </DrillValue>
  )
}
